import { useCallback, useMemo, useState } from "react";
import type {
  AskQuestionFormValues,
  CreateWorkflowFormValues,
  EntityCandidate,
  EntityResolveStatus,
  ExistingEntityWorkflow,
} from "@compositions/index";
import {
  useAskQuestion,
  useCreateWorkflow,
  useWorkflowKinds,
  useWorkflowsByEntity,
} from "../workflows";
import { useEntityCandidates, useEntityResolve } from "../entities";

/**
 * State and handlers for the Create Workflow and Ask Question dialogs.
 *
 * The hook owns dialog visibility, submit errors, the entity search that
 * feeds the create dialog, and the kind filter. Navigation after a
 * successful create or ask is delegated to `navigateToWorkflow`.
 */

export interface WorkflowTarget {
  readonly id: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly compose?: "task";
}

export interface CreateDialogState {
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly error: string | null;
  readonly availableKinds: readonly string[];
  readonly entityCandidates: readonly EntityCandidate[];
  readonly entityCandidatesLoading: boolean;
  readonly entityResolveStatus: EntityResolveStatus;
  readonly entityResolveLoading: boolean;
  readonly existingWorkflows: readonly ExistingEntityWorkflow[];
  readonly existingWorkflowsLoading: boolean;
  readonly open: () => void;
  readonly close: () => void;
  readonly submit: (values: CreateWorkflowFormValues) => void;
  readonly onEntitySearch: (type: string, query: string) => void;
  readonly onEntityResolve: (type: string, id: string) => void;
  readonly onOpenExisting: (workflow: ExistingEntityWorkflow) => void;
}

export interface AskDialogState {
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly error: string | null;
  readonly open: () => void;
  readonly close: () => void;
  readonly submit: (values: AskQuestionFormValues) => void;
}

export interface WorkflowDialogs {
  readonly create: CreateDialogState;
  readonly ask: AskDialogState;
}

export function useWorkflowDialogs(
  navigateToWorkflow: (target: WorkflowTarget) => void,
  selectedQueueId: string | null,
): WorkflowDialogs {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const createMutation = useCreateWorkflow();
  const kindsQuery = useWorkflowKinds();

  // "ad-hoc" is owned by the Ask flow and "child-thread" by the @-mention
  // composer. Neither is a valid manual pick, so the picker omits them.
  const availableKinds = useMemo(
    () => (kindsQuery.data?.kinds ?? []).filter((k) => k !== "ad-hoc" && k !== "child-thread"),
    [kindsQuery.data?.kinds],
  );

  const [entitySearchType, setEntitySearchType] = useState("");
  const [entitySearchQuery, setEntitySearchQuery] = useState("");
  const [entityResolveType, setEntityResolveType] = useState("");
  const [entityResolveId, setEntityResolveId] = useState("");

  const entityCandidatesQuery = useEntityCandidates(entitySearchType, entitySearchQuery);
  const entityResolveQuery = useEntityResolve(
    entityResolveType,
    entityResolveId,
    entityResolveType.length > 0 && entityResolveId.length > 0,
  );

  const entityWorkflowsQuery = useWorkflowsByEntity(entityResolveType, entityResolveId);
  const existingWorkflows = useMemo<readonly ExistingEntityWorkflow[]>(
    () =>
      (entityWorkflowsQuery.data?.items ?? [])
        .filter((workflow) => workflow.parentWorkflowId === null)
        .map((workflow) => ({
          id: workflow.id,
          title: workflow.title,
          kind: workflow.kind,
          entityType: workflow.entityType,
          entityId: workflow.entityId,
        })),
    [entityWorkflowsQuery.data?.items],
  );

  const onEntitySearch = useCallback((type: string, query: string) => {
    setEntitySearchType(type);
    setEntitySearchQuery(query);
  }, []);

  const onEntityResolve = useCallback((type: string, id: string) => {
    setEntityResolveType(type);
    setEntityResolveId(id);
  }, []);

  const openCreate = useCallback(() => {
    setCreateError(null);
    setEntitySearchType("");
    setEntitySearchQuery("");
    setEntityResolveType("");
    setEntityResolveId("");
    setIsCreateOpen(true);
  }, []);

  const closeCreate = useCallback(() => {
    if (createMutation.isPending) return;
    setIsCreateOpen(false);
  }, [createMutation.isPending]);

  const submitCreate = useCallback(
    (values: CreateWorkflowFormValues) => {
      setCreateError(null);
      createMutation.mutate(
        {
          workflowId: values.workflowId,
          entityType: values.entityType,
          entityId: values.entityId,
          entityDisplayName: values.entityDisplayName,
          kind: values.kind,
          title: values.title,
        },
        {
          onSuccess: (created) => {
            setIsCreateOpen(false);
            navigateToWorkflow({
              id: created.id,
              entityType: created.entityType,
              entityId: created.entityId,
              compose: "task",
            });
          },
          onError: (error: unknown) => {
            setCreateError(error instanceof Error ? error.message : "Failed to create workflow.");
          },
        },
      );
    },
    [createMutation, navigateToWorkflow],
  );

  const openExisting = useCallback(
    (workflow: ExistingEntityWorkflow) => {
      setIsCreateOpen(false);
      navigateToWorkflow({
        id: workflow.id,
        entityType: workflow.entityType,
        entityId: workflow.entityId,
      });
    },
    [navigateToWorkflow],
  );

  const [isAskOpen, setIsAskOpen] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const askMutation = useAskQuestion();

  const openAsk = useCallback(() => {
    setAskError(null);
    setIsAskOpen(true);
  }, []);

  const closeAsk = useCallback(() => {
    if (askMutation.isPending) return;
    setIsAskOpen(false);
  }, [askMutation.isPending]);

  const submitAsk = useCallback(
    (values: AskQuestionFormValues) => {
      setAskError(null);
      // The dialog's "From" choice wins. Without one, a shared queue selected
      // in the rail owns the question; otherwise the server falls back to the
      // asker's personal queue.
      const originatingQueueId = values.originatingQueueId ?? selectedQueueId ?? undefined;
      askMutation.mutate(
        {
          workflowId: values.workflowId,
          recipientQueueIds: values.recipientQueueIds,
          body: values.body,
          ...(originatingQueueId !== undefined ? { originatingQueueId } : {}),
        },
        {
          onSuccess: (created) => {
            setIsAskOpen(false);
            navigateToWorkflow({
              id: created.id,
              entityType: created.entityType,
              entityId: created.entityId,
            });
          },
          onError: (error: unknown) => {
            setAskError(error instanceof Error ? error.message : "Failed to send question.");
          },
        },
      );
    },
    [askMutation, navigateToWorkflow, selectedQueueId],
  );

  return {
    create: {
      isOpen: isCreateOpen,
      isSubmitting: createMutation.isPending,
      error: createError,
      availableKinds,
      entityCandidates: entityCandidatesQuery.data?.items ?? [],
      entityCandidatesLoading: entityCandidatesQuery.isLoading,
      entityResolveStatus: entityResolveQuery.data?.status ?? null,
      entityResolveLoading: entityResolveQuery.isLoading,
      existingWorkflows,
      existingWorkflowsLoading: entityWorkflowsQuery.isLoading,
      open: openCreate,
      close: closeCreate,
      submit: submitCreate,
      onEntitySearch,
      onEntityResolve,
      onOpenExisting: openExisting,
    },
    ask: {
      isOpen: isAskOpen,
      isSubmitting: askMutation.isPending,
      error: askError,
      open: openAsk,
      close: closeAsk,
      submit: submitAsk,
    },
  };
}
